// What a car's TUNE MFD recorded and sent: its trips, its track days and its datalogs, a view each. A trip or a track
// day opens to its figures in the car's units; a datalog opens in the Analyser, downloads, or goes. Each is kept for
// 12 months.
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { METRIC, type components, type Units } from '@tune/shared';
import { useSearchParams } from 'react-router';
import { data, problem } from '../../api';
import { useAuth } from '../../auth';
import { fetchFile, size } from '../../items';
import { distanceText, durationText, speedText } from '../../lib/car';
import { forgetDatalog } from '../../lib/datalogs';
import { deviceRefName, lapText, whenText } from '../../lib/words';
import { keys, useDevices, useUnits } from '../../queries';
import {
  Badge,
  Banner,
  Button,
  ButtonLink,
  EmptyState,
  ErrorBanner,
  IconButton,
  ListButton,
  ListSection,
  ListValue,
  Menu,
  SegmentedControl,
  Sheet,
  Skeleton,
  useConfirm,
  useNow,
  useRouteSheet,
  useToast,
} from '../../ui';
import {
  CaretRightIcon,
  ChartLineIcon,
  DotsThreeIcon,
  DownloadSimpleIcon,
  FlagCheckeredIcon,
  PathIcon,
  TrashIcon,
} from '../../ui/icons';
import { CarPage } from './CarPage';
import s from './Recordings.module.css';

type Vehicle = components['schemas']['Vehicle'];
type Summary = components['schemas']['RecordSummary'];
type Datalog = components['schemas']['Datalog'];

const PAGE = 20;
const chevron = <CaretRightIcon size={16} weight="bold" aria-hidden="true" />;

type View = 'trips' | 'laps' | 'datalogs';
const VIEWS: ReadonlyArray<{ value: View; label: string }> = [
  { value: 'trips', label: 'Trips' },
  { value: 'laps', label: 'Track days' },
  { value: 'datalogs', label: 'Datalogs' },
];

export function Recordings() {
  return <CarPage section="Recordings">{(car) => <RecordingsOf car={car} />}</CarPage>;
}

function RecordingsOf({ car }: { car: Vehicle }) {
  const [params, setParams] = useSearchParams();
  const asked = params.get('show');
  const view: View = VIEWS.some((v) => v.value === asked) ? (asked as View) : 'trips';
  const units = useUnits(car.id) ?? METRIC;
  return (
    <>
      <p className={s.lead}>
        What the car's TUNE MFD sent, while it's signed in and the car is stopped. Each is kept for 12 months.
      </p>
      <SegmentedControl
        label="What to show"
        wide
        segments={VIEWS}
        value={view}
        onChange={(next) =>
          setParams(
            (p) => {
              p.set('show', next);
              return p;
            },
            { replace: true },
          )
        }
      />
      {view === 'datalogs' ? (
        <Datalogs car={car} />
      ) : (
        <Records key={view} car={car} kind={view === 'laps' ? 'lap' : 'trip'} units={units} />
      )}
    </>
  );
}

/** Trips, or track days: each opens to its figures. */
function Records({ car, kind, units }: { car: Vehicle; kind: 'trip' | 'lap'; units: Units }) {
  const api = useAuth().api('owners');
  const devices = useDevices();
  const now = useNow(60_000);
  const list = useInfiniteQuery({
    queryKey: [...keys.records(car.id), kind],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      data(
        api.GET('/vehicles/{vehicleId}/records', {
          params: {
            path: { vehicleId: car.id },
            query: { kind, limit: PAGE, ...(pageParam ? { cursor: pageParam } : {}) },
          },
        }),
      ),
    getNextPageParam: (last) => last.next ?? undefined,
  });
  const records = list.data?.pages.flatMap((p) => p.records) ?? [];
  const trips = kind === 'trip';
  return (
    <>
      {list.isPending && (
        <Skeleton blocks={2} lines={0} label={trips ? 'Loading the trips' : 'Loading the track days'} />
      )}
      {list.isError && (
        <ErrorBanner retry={() => void list.refetch()}>
          {trips ? "The trips couldn't load." : "The track days couldn't load."}
        </ErrorBanner>
      )}
      {list.isSuccess && records.length === 0 && (
        <EmptyState
          icon={trips ? <PathIcon size={56} /> : <FlagCheckeredIcon size={56} />}
          title={trips ? 'No trips yet' : 'No track days yet'}
        >
          {trips
            ? 'The MFD sends each trip as it ends, with its distance, time and speeds.'
            : 'A lap-timing session with laps in it is a track day. The MFD sends it as the session ends.'}
        </EmptyState>
      )}
      {records.length > 0 && (
        <ListSection>
          {records.map((r) => (
            <RecordRow
              key={r.id}
              car={car}
              record={r}
              units={units}
              from={r.device ? deviceRefName(r.device, devices.data?.devices ?? []) : null}
              now={now}
            />
          ))}
        </ListSection>
      )}
      {list.hasNextPage && (
        <div>
          <Button variant="gray" busy={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
            {trips ? 'Show older trips' : 'Show older track days'}
          </Button>
        </div>
      )}
    </>
  );
}

function RecordRow({
  car,
  record,
  units,
  from,
  now,
}: {
  car: Vehicle;
  record: Summary;
  units: Units;
  from: string | null;
  now: number;
}) {
  const [open, setOpen] = useRouteSheet(`record-${record.id}`);
  return (
    <>
      <ListButton
        label={whenText(record.startedAt, now)}
        subtitle={from ? `From ${from}` : undefined}
        accessory={chevron}
        onClick={() => setOpen(true)}
      />
      <RecordSheet car={car} record={record} units={units} open={open} onClose={() => setOpen(false)} />
    </>
  );
}

interface Lap {
  number: number;
  time: number;
  sectors?: number[];
}

/** A trip's or a track day's figures, as TUNE Live records them, with Delete. */
function RecordSheet({
  car,
  record,
  units,
  open,
  onClose,
}: {
  car: Vehicle;
  record: Summary;
  units: Units;
  open: boolean;
  onClose: () => void;
}) {
  const api = useAuth().api('owners');
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const say = useToast();
  const path = { params: { path: { vehicleId: car.id, recordId: record.id } } };
  const detail = useQuery({
    queryKey: [...keys.records(car.id), 'one', record.id],
    queryFn: () => data(api.GET('/vehicles/{vehicleId}/records/{recordId}', path)),
    enabled: open,
  });
  const trip = record.kind === 'trip';
  const remove = useMutation({
    mutationFn: () => data(api.DELETE('/vehicles/{vehicleId}/records/{recordId}', path)),
    onSuccess: async () => {
      onClose();
      await queryClient.invalidateQueries({ queryKey: keys.records(car.id) });
      say(trip ? 'The trip is deleted.' : 'The track day is deleted.');
    },
  });
  const ask = async () => {
    const yes = await confirm({
      title: trip ? 'Delete this trip?' : 'Delete this track day?',
      message: `It goes from ${car.nickname}'s recordings for good.`,
      confirm: 'Delete',
    });
    if (yes) {
      remove.mutate();
    }
  };
  const body = (detail.data?.body ?? {}) as Record<string, unknown>;
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  const laps = Array.isArray(body.laps) ? (body.laps as Lap[]) : [];
  const best = num(body.best);
  return (
    <Sheet
      open={open}
      onClose={() => {
        remove.reset();
        onClose();
      }}
      title={trip ? 'Trip' : 'Track day'}
      cancelLabel="Close"
      footer={
        <div>
          <Button
            variant="destructivePlain"
            icon={<TrashIcon size={18} weight="bold" />}
            busy={remove.isPending}
            onClick={() => void ask()}
          >
            {trip ? 'Delete the trip' : 'Delete the track day'}
          </Button>
        </div>
      }
    >
      {remove.isError && <Banner tone="error">{problem(remove.error)}</Banner>}
      {detail.isPending && <Skeleton lines={4} label="Loading its figures" />}
      {detail.isError && <ErrorBanner retry={() => void detail.refetch()}>Its figures couldn't load.</ErrorBanner>}
      {detail.data &&
        (trip ? (
          <ListSection level={3}>
            <ListValue label="Started" detail={whenText(record.startedAt)} />
            {typeof body.trip === 'string' && <ListValue label="Trip" detail={body.trip} />}
            {num(body.distanceKm) !== null && (
              <ListValue label="Distance" detail={distanceText(units, num(body.distanceKm) ?? 0)} />
            )}
            {num(body.timeS) !== null && <ListValue label="Time" detail={durationText(num(body.timeS) ?? 0)} />}
            {num(body.maxKmh) !== null && (
              <ListValue label="Top speed" detail={speedText(units, num(body.maxKmh) ?? 0)} />
            )}
            {num(body.averageKmh) !== null && (
              <ListValue label="Average speed" detail={speedText(units, num(body.averageKmh) ?? 0)} />
            )}
            {num(body.odometerKm) !== null && (
              <ListValue label="Odometer at the end" detail={distanceText(units, num(body.odometerKm) ?? 0)} />
            )}
          </ListSection>
        ) : (
          <>
            <ListSection level={3}>
              <ListValue label="Started" detail={whenText(record.startedAt)} />
              {typeof (body.track as { name?: unknown } | undefined)?.name === 'string' && (
                <ListValue label="Track" detail={String((body.track as { name: string }).name)} />
              )}
              <ListValue label="Laps" detail={String(laps.length)} />
              {best !== null && <ListValue label="Best lap" detail={lapText(best)} />}
            </ListSection>
            {laps.length > 0 && (
              <ListSection header="Each lap" level={3}>
                {laps.map((lap) => (
                  <ListValue
                    key={lap.number}
                    label={`Lap ${lap.number}`}
                    subtitle={
                      (lap.sectors ?? []).length > 1
                        ? `Sectors ${(lap.sectors ?? []).map((t) => t.toFixed(3)).join(' · ')}`
                        : undefined
                    }
                    detail={
                      <span className={s.lap}>
                        {lap.time === best && <Badge tone="accent">Best</Badge>}
                        {lapText(lap.time)}
                      </span>
                    }
                  />
                ))}
              </ListSection>
            )}
          </>
        ))}
    </Sheet>
  );
}

/** The car's datalogs: each opens in the Analyser, downloads, or goes. */
function Datalogs({ car }: { car: Vehicle }) {
  const api = useAuth().api('owners');
  const devices = useDevices();
  const now = useNow(60_000);
  const list = useInfiniteQuery({
    queryKey: [...keys.datalogs(car.id), 'all'],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      data(
        api.GET('/vehicles/{vehicleId}/datalogs', {
          params: { path: { vehicleId: car.id }, query: { limit: PAGE, ...(pageParam ? { cursor: pageParam } : {}) } },
        }),
      ),
    getNextPageParam: (last) => last.next ?? undefined,
  });
  const logs = list.data?.pages.flatMap((p) => p.datalogs) ?? [];
  return (
    <>
      {list.isPending && <Skeleton blocks={2} lines={0} label="Loading the datalogs" />}
      {list.isError && <ErrorBanner retry={() => void list.refetch()}>The datalogs couldn't load.</ErrorBanner>}
      {list.isSuccess && logs.length === 0 && (
        <EmptyState icon={<ChartLineIcon size={56} />} title="No datalogs yet">
          Record on the MFD, on its Datalogs page. It sends each datalog once it's on Wi-Fi.
        </EmptyState>
      )}
      {logs.length > 0 && (
        <ListSection footer="CSV files, gzipped. Analyse opens one with its charts, map and laps.">
          {logs.map((d) => (
            <DatalogRow
              key={d.id}
              car={car}
              datalog={d}
              from={d.device ? deviceRefName(d.device, devices.data?.devices ?? []) : null}
              now={now}
            />
          ))}
        </ListSection>
      )}
      {list.hasNextPage && (
        <div>
          <Button variant="gray" busy={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
            Show older datalogs
          </Button>
        </div>
      )}
    </>
  );
}

function DatalogRow({ car, datalog, from, now }: { car: Vehicle; datalog: Datalog; from: string | null; now: number }) {
  const api = useAuth().api('owners');
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const say = useToast();
  const path = { params: { path: { vehicleId: car.id, datalogId: datalog.id } } };
  const when = whenText(datalog.startedAt ?? datalog.receivedAt, now);
  const download = useMutation({
    mutationFn: () => data(api.GET('/vehicles/{vehicleId}/datalogs/{datalogId}/download', path)),
    onSuccess: (link) => fetchFile(link.url),
  });
  const remove = useMutation({
    mutationFn: () => data(api.DELETE('/vehicles/{vehicleId}/datalogs/{datalogId}', path)),
    onSuccess: async () => {
      await forgetDatalog(datalog.id);
      await queryClient.invalidateQueries({ queryKey: keys.datalogs(car.id) });
      say('The datalog is deleted.');
    },
  });
  const ask = async () => {
    const yes = await confirm({
      title: 'Delete this datalog?',
      message: `${datalog.fileName} goes from TUNE's cloud for good. A copy on the MFD stays there.`,
      confirm: 'Delete',
    });
    if (yes) {
      remove.mutate();
    }
  };
  const failed = download.error ?? remove.error;
  return (
    <div className={s.datalog}>
      <div className={s.datalogText}>
        <span className={s.datalogWhen}>{when}</span>
        <span className={s.datalogMeta}>
          {[datalog.fileName, size(datalog.size), from ? `from ${from}` : null].filter(Boolean).join(' · ')}
        </span>
        {failed && <span className={s.datalogError}>{problem(failed)}</span>}
      </div>
      <div className={s.datalogActions}>
        <ButtonLink to={`/telemetry/${car.id}/${datalog.id}`} variant="tinted" size="small">
          Analyse<span className="visually-hidden"> the datalog of {when}</span>
        </ButtonLink>
        <Menu
          label={`The datalog of ${when}`}
          entries={[
            {
              label: 'Download',
              icon: <DownloadSimpleIcon size={18} weight="bold" />,
              onSelect: () => download.mutate(),
            },
            {
              label: 'Delete',
              icon: <TrashIcon size={18} weight="bold" />,
              destructive: true,
              onSelect: () => void ask(),
            },
          ]}
          trigger={(props) => (
            <IconButton
              {...props}
              label={`More for the datalog of ${when}`}
              icon={<DotsThreeIcon size={20} weight="bold" />}
            />
          )}
        />
      </div>
    </div>
  );
}
